'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { setLocale as setLocaleModule } from '@/locales';

interface LocaleContextValue {
  locale: string;
  setLocale: (locale: string) => void;
}

const STORAGE_KEY = 'insulacharter_locale';
const VALID_LOCALES = ['it', 'en', 'de', 'es', 'pt'];

const LocaleContext = createContext<LocaleContextValue>({
  locale: 'it',
  setLocale: () => {},
});

export const useLocale = () => useContext(LocaleContext);

export default function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState('it');
  const wrapperRef = useRef<HTMLDivElement>(null);

  // On mount: read saved locale
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && VALID_LOCALES.includes(saved) && saved !== 'it') {
      setLocaleModule(saved);
      setLocaleState(saved);
    }
  }, []);

  const setLocale = useCallback((code: string) => {
    if (!VALID_LOCALES.includes(code)) return;
    // Fade out before switching
    const el = wrapperRef.current;
    if (el) el.style.opacity = '0';
    // After short fade, switch locale and fade back in
    setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, code);
      setLocaleModule(code);
      setLocaleState(code);
    }, 80);
  }, []);

  // Fade back in after locale changes (key change re-creates DOM)
  useEffect(() => {
    const el = wrapperRef.current;
    if (el) {
      // Start at 0 and fade in
      el.style.opacity = '0';
      requestAnimationFrame(() => { el.style.opacity = '1'; });
    }
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);

  // Use locale as key to force re-render of the entire tree when language changes.
  // This ensures all t() calls re-evaluate with the new locale.
  // The ref + opacity transition prevents white flash during remount.
  return (
    <LocaleContext.Provider value={value}>
      <div
        key={locale}
        ref={wrapperRef}
        style={{ transition: 'opacity 120ms ease-in-out', opacity: 1 }}
      >
        {children}
      </div>
    </LocaleContext.Provider>
  );
}
